import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalKeyTrap } from '../../hooks/useModalKeyTrap';
import '../ExitModal/ExitModal.css';

interface ConfirmationModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [focusedButton, setFocusedButton] = useState<'cancel' | 'confirm'>('cancel');

  useEffect(() => {
    if (focusedButton === 'cancel') cancelRef.current?.focus();
    else confirmRef.current?.focus();
  }, [focusedButton]);

  useModalKeyTrap(useCallback((key, event) => {
    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      event.preventDefault();
      setFocusedButton('cancel');
    } else if (key === 'ArrowRight' || key === 'ArrowDown') {
      event.preventDefault();
      setFocusedButton('confirm');
    } else if (key === 'Enter' || key === 'Space') {
      event.preventDefault();
      if (focusedButton === 'cancel') onCancel();
      else onConfirm();
    } else if (key === 'Back') {
      event.preventDefault();
      onCancel();
    }
  }, [focusedButton, onCancel, onConfirm]));

  return (
    <div className="exitmodal__overlay" onClick={onCancel}>
      <div
        className="exitmodal__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cleanup-confirm-title"
        onClick={(event) => event.stopPropagation()}
      >
        <p id="cleanup-confirm-title" className="exitmodal__title">{title}</p>
        <p className="settings-cleanup-row__desc">{message}</p>
        <div className="exitmodal__actions">
          <button
            ref={cancelRef}
            className={`exitmodal__btn exitmodal__btn--no${focusedButton === 'cancel' ? ' exitmodal__btn--focused' : ''}`}
            onClick={onCancel}
            autoFocus
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            ref={confirmRef}
            className={`exitmodal__btn exitmodal__btn--yes${focusedButton === 'confirm' ? ' exitmodal__btn--focused' : ''}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
